import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { FiCheck, FiInfo, FiAlertTriangle, FiX } from "react-icons/fi";

// Toast container that stacks toasts from the top
const ToastContainer = styled.div`
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  transition: all 0.3s ease;
`;

// Position variants for the toast container
const positionStyles: Record<string, React.CSSProperties> = {
  top: { top: 0, left: "50%", transform: "translateX(-50%)" },
  "top-right": { top: 0, right: 0 },
  "top-left": { top: 0, left: 0 },
  bottom: { bottom: 0, left: "50%", transform: "translateX(-50%)" },
  "bottom-right": { bottom: 0, right: 0 },
  "bottom-left": { bottom: 0, left: 0 },
};

// Individual toast styling
const ToastItem = styled.div<{
  status: "success" | "error" | "warning" | "info";
}>`
  display: flex;
  align-items: flex-start;
  background: ${(props) => {
    switch (props.status) {
      case "success":
        return "#1a7f4b";
      case "error":
        return "#d32f2f";
      case "warning":
        return "#f57c00";
      case "info":
      default:
        return "#0288d1";
    }
  }};
  color: white;
  border-radius: 6px;
  padding: 12px 16px;
  min-width: 300px;
  max-width: 500px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.3s ease forwards;
  position: relative;

  @keyframes slideIn {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const IconWrapper = styled.div`
  margin-right: 12px;
  display: flex;
  align-items: center;
  font-size: 20px;
`;

const ContentWrapper = styled.div`
  flex: 1;
`;

const Title = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const Description = styled.div`
  font-size: 14px;
  opacity: 0.9;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

// Toast types
export interface ToastProps {
  id?: string;
  title: string;
  description?: string;
  status?: "success" | "error" | "warning" | "info";
  duration?: number;
  isClosable?: boolean;
  position?:
    | "top"
    | "top-right"
    | "top-left"
    | "bottom"
    | "bottom-right"
    | "bottom-left";
  onClose?: () => void;
}

// Store for active toasts
let toasts: ToastProps[] = [];
let listeners: (() => void)[] = [];

// Notify listeners when toasts change
const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

// Add a new toast
export const toast = (props: ToastProps) => {
  const id = Math.random().toString(36).substring(2, 9);
  const newToast: ToastProps = {
    id,
    status: "info" as "info",
    duration: 5000,
    isClosable: true,
    position: "bottom-right" as "bottom-right",
    ...props,
  };

  toasts = [...toasts, newToast];
  notifyListeners();

  // Auto-remove toast after duration
  if (newToast.duration !== Infinity) {
    setTimeout(() => {
      closeToast(id);
    }, newToast.duration);
  }

  return id;
};

// Close a toast by ID
export const closeToast = (id: string) => {
  toasts = toasts.filter((toast) => toast.id !== id);
  notifyListeners();
};

// Close all toasts
export const closeAllToasts = () => {
  toasts = [];
  notifyListeners();
};

// Toast provider component
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [, setForceUpdate] = useState({});

  useEffect(() => {
    const listener = () => setForceUpdate({});
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  // Group toasts by position
  const toastsByPosition = toasts.reduce((acc, toast) => {
    const position = toast.position || "bottom-right";
    if (!acc[position]) {
      acc[position] = [];
    }
    acc[position].push(toast);
    return acc;
  }, {} as Record<string, ToastProps[]>);

  // Get the appropriate icon based on status
  const getStatusIcon = (status: "success" | "error" | "warning" | "info") => {
    switch (status) {
      case "success":
        return <FiCheck />;
      case "error":
        return <FiX />;
      case "warning":
        return <FiAlertTriangle />;
      case "info":
      default:
        return <FiInfo />;
    }
  };

  // Create portal for each position group
  return (
    <>
      {children}
      {Object.entries(toastsByPosition).map(([position, positionToasts]) => {
        return createPortal(
          <ToastContainer style={{ ...positionStyles[position] }}>
            {positionToasts.map((toast) => (
              <ToastItem key={toast.id} status={toast.status || "info"}>
                <IconWrapper>
                  {getStatusIcon(toast.status || "info")}
                </IconWrapper>
                <ContentWrapper>
                  <Title>{toast.title}</Title>
                  {toast.description && (
                    <Description>{toast.description}</Description>
                  )}
                </ContentWrapper>
                {toast.isClosable && (
                  <CloseButton
                    onClick={() => {
                      closeToast(toast.id!);
                      if (toast.onClose) toast.onClose();
                    }}
                  >
                    <FiX />
                  </CloseButton>
                )}
              </ToastItem>
            ))}
          </ToastContainer>,
          document.body
        );
      })}
    </>
  );
};
